/**
 * Unified Analytics Module for AICarma
 * Events flow: Frontend → Segment → Mixpanel (as destination)
 * 
 * Mixpanel is loaded separately in loadMetrics.ts for:
 * - Session recording (record_sessions_percent: 1)
 * - Heatmap data
 */

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: Record<string, unknown>[];
    analytics?: {
      track: (event: string, properties?: Record<string, unknown>) => void;
      page: (name?: string, properties?: Record<string, unknown>) => void;
      identify: (userId: string, traits?: Record<string, unknown>) => void;
      reset: () => void;
      initialized?: boolean;
    };
    mixpanel?: {
      track: (event: string, properties?: Record<string, unknown>) => void;
      identify: (distinctId: string) => void;
      people: {
        set: (properties: Record<string, unknown>) => void;
        set_once: (properties: Record<string, unknown>) => void;
        increment: (property: string, value?: number) => void;
      };
      reset: () => void;
      time_event: (eventName: string) => void;
      register: (properties: Record<string, unknown>) => void;
    };
  }
}

// =============================================================================
// Event Types for Type Safety
// =============================================================================

export type AnalyticsEventName =
  // Acquisition Events
  | "Main page visited"
  | "Promo video clicked"
  | "Blog page visited"
  // Registration Funnel
  | "User registered"
  | "Input brand name"
  | "Competitors suggested"
  | "Competitors confirmed"
  // Core Product Events
  | "Add competitor"
  | "Delete competitor"
  | "Brand created"
  | "Report viewed"
  // Conversion Events
  | "Pricing page viewed"
  | "Checkout started"
  | "Subscription success"
  | "Subscription cancelled";

export interface AnalyticsEventProperties {
  brand_name?: string;
  brand_id?: number;
  competitor_name?: string;
  competitor_id?: string;
  competitor_count?: number;
  plan_type?: string;
  price?: number;
  currency?: string;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  [key: string]: unknown;
}

export interface UserTraits {
  email?: string;
  first_name?: string;
  last_name?: string;
  brand?: string;
  created_at?: string;
  is_subscribed?: boolean;
  plan_type?: string;
  [key: string]: unknown;
}

// =============================================================================
// Analytics Instance
// =============================================================================

interface AnalyticsInstance {
  track: (event: AnalyticsEventName | string, properties?: AnalyticsEventProperties) => void;
  page: (pageName?: string, properties?: Record<string, unknown>) => void;
  identify: (userId: string, traits?: UserTraits) => void;
  setUserProperties: (properties: Record<string, unknown>) => void;
  reset: () => void;
}

const createAnalyticsInstance = (): AnalyticsInstance => {
  const isProduction = import.meta.env.MODE === "production";

  if (!isProduction) {
    return {
      track: () => {},
      page: () => {},
      identify: () => {},
      setUserProperties: () => {},
      reset: () => {},
    };
  }

  // Production: Send events directly to Mixpanel SDK
  // Mixpanel SDK tracks with $device_id which gets merged with user_id on identify()
  return {
    track: (event: AnalyticsEventName | string, properties?: AnalyticsEventProperties) => {
      // Send directly to Mixpanel SDK for ID Merge to work properly
      // This ensures events use Mixpanel's $device_id which gets merged with user_id
      if (window.mixpanel?.track) {
        window.mixpanel.track(event, properties);
      }
    },

    page: (_pageName?: string, properties?: Record<string, unknown>) => {
      // PageTracker component now handles pageview tracking
      // This method is kept for backwards compatibility
      if (window.mixpanel?.track) {
        window.mixpanel.track('Viewed', {
          path: window.location.pathname,
          title: document.title,
          ...properties,
        });
      }
    },

    identify: (userId: string, traits?: UserTraits) => {
      // Identify user in Mixpanel for session recording and ID merge
      if (window.mixpanel?.identify) {
        window.mixpanel.identify(userId);
        if (traits && window.mixpanel.people) {
          window.mixpanel.people.set({
            $email: traits.email,
            $first_name: traits.first_name,
            $last_name: traits.last_name,
          });
        }
      }
    },

    // Set custom user profile properties in Mixpanel
    setUserProperties: (properties: Record<string, unknown>) => {
      if (window.mixpanel?.people) {
        window.mixpanel.people.set(properties);
      }
    },

    reset: () => {
      if (window.mixpanel?.reset) {
        window.mixpanel.reset();
      }
    },
  };
};

const analytics = createAnalyticsInstance();

export default analytics;
